import { extractUnitOptions } from '#/modules/imsMgmt/helper/materialUnit/extractUnitOptions';
import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import {
  getInvStockAvailableQty,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import {
  STOCK_ISSUE_HEADER_SOURCE_TYPE,
  STOCK_ISSUE_LINE_SOURCE_TYPE,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueSourceType';

export { maxIssueQtyInUnit } from './issueQtyBounds';

function formatWarehouseLocationLabel(warehouse) {
  if (!warehouse) {
    return '';
  }

  const warehouseLabel = warehouse.name ?? '';
  const location = warehouse.location;
  if (!location) {
    return warehouseLabel;
  }

  const locationLabel = location.name ?? '';

  return [warehouseLabel, locationLabel].filter(Boolean).join(' / ');
}

export function createEmptyIssueLine() {
  return {
    sourceType: STOCK_ISSUE_LINE_SOURCE_TYPE,
    invStockId: '',
    stockNo: '',
    mtrId: '',
    mtrNo: '',
    mtrName: '',
    warehouseLabel: '',
    availableQty: 0,
    unitId: '',
    unitOptions: [],
    qty: '',
    remark: '',
  };
}

/**
 * 庫存列表列 → 出庫明細列（預設出庫量 = min(1, availableBase)）。
 * unitId 先空，options 就緒後由 UI 套 base。
 * availableQty 語意＝基本單位可用量。
 */
export function mapInvStockRowToIssueLine(row) {
  if (!row?.id) {
    return null;
  }

  const availableQty = getInvStockAvailableQty(row);
  const defaultQty = availableQty > 0 ? Math.min(1, availableQty) : 1;
  const unitOptions = extractUnitOptions(row.mtr ?? row);

  return {
    sourceType: STOCK_ISSUE_LINE_SOURCE_TYPE,
    invStockId: row.id,
    stockNo: row.stockNo ?? '',
    mtrId: row.mtr?.id ?? '',
    mtrNo: row.mtr?.mtrNo ?? '',
    mtrName: row.mtr?.name ?? '',
    warehouseLabel: formatWarehouseLocationLabel(row.warehouse),
    availableQty,
    unitId: '',
    unitOptions,
    qty: defaultQty,
    remark: '',
  };
}

/**
 * 表單 → create／update body。
 * Header 不送 sourceType／sourceId（後端自填 MANUAL／null）。
 * Line：INVENTORY_STOCK＋invStockId＋qty（正整數）＋可選 unitId＋remark。
 */
export function buildStockIssueWritePayload(values, { updatedAt = null } = {}) {
  const payload = {
    issueDate: values.issueDate || null,
    stockIssueCategoryId: values.stockIssueCategoryId || null,
    issueReason: values.issueReason || null,
    remark: values.remark || null,
    lines: (values.lines ?? []).map((line) => {
      const linePayload = {
        sourceType: STOCK_ISSUE_LINE_SOURCE_TYPE,
        invStockId: line.invStockId,
        qty: line.qty === '' || line.qty == null ? null : Number(line.qty),
        remark: line.remark || null,
      };

      const unitId = String(line.unitId ?? '').trim();
      if (unitId) {
        linePayload.unitId = unitId;
      }

      return linePayload;
    }),
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

/**
 * detail → 表單。回填 qty／unit.id（不自行換算）。
 * convert_rate／conversion_rate 僅後端 snapshot，表單不存。
 */
export function mapStockIssueDetailToFormValues(detail) {
  return {
    issueDate: detail?.issueDate ?? '',
    stockIssueCategoryId: detail?.category?.id
      ?? detail?.stockIssueCategoryId
      ?? '',
    sourceType: detail?.sourceType ?? STOCK_ISSUE_HEADER_SOURCE_TYPE,
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: (detail?.lines ?? []).map((line) => {
      const invStock = line.invStock;
      const availableQty = Number(
        invStock?.availableQty ?? invStock?.baseQty ?? line.baseQty ?? 0,
      );
      const unitOptions = extractUnitOptions(line);

      return {
        sourceType: line.sourceType ?? STOCK_ISSUE_LINE_SOURCE_TYPE,
        invStockId: invStock?.id ?? line.invStockId ?? '',
        stockNo: invStock?.stockNo ?? '',
        mtrId: line.mtr?.id ?? invStock?.mtr?.id ?? '',
        mtrNo: line.mtr?.mtrNo ?? invStock?.mtr?.mtrNo ?? '',
        mtrName: line.mtr?.name ?? invStock?.mtr?.name ?? '',
        warehouseLabel: formatWarehouseLocationLabel(
          line.warehouse ?? invStock?.warehouse,
        ),
        availableQty: Number.isFinite(availableQty) ? availableQty : 0,
        unitId: resolveUnitId(line.unit) || resolveUnitId(line),
        unitOptions,
        qty: line.qty ?? '',
        remark: line.remark ?? '',
      };
    }),
  };
}
